import { useState, useEffect, useCallback } from 'react';
import { getRecentStationEpisodes, getRecentShowEpisodes } from '../../api/client';
import { parseEpisodeTitle, formatEpisodeDate } from '../../utils/time';
import { HiPlay, HiPlus, HiCheck } from 'react-icons/hi2';
import { ContinueListening } from './ContinueListening';
import type { RecentEpisode, Stream, Show } from '../../types';
import type { HistoryItem } from '../../hooks/usePlaybackHistory';

interface SidebarProps {
  stream: Stream;
  show: Show;
  currentEpisodeId: number;
  queuedEpisodeIds: number[];
  onAddToQueue: (episode: RecentEpisode) => void;
  onNavigate?: (episodeId: number) => void;
  isLiveMode?: boolean;
  historyItems?: HistoryItem[];
  onRemoveFromHistory?: (episodeId: number) => void;
}

interface EpisodeItemProps {
  episode: RecentEpisode;
  fallbackImage: string;
  isCurrentEpisode: boolean;
  isInQueue: boolean;
  onAddToQueue: () => void;
  onNavigate?: (episodeId: number) => void;
  displayMode?: 'date' | 'title'; // 'date' for same-show lists, 'title' for mixed-show lists
}

const INITIAL_LIMIT = 10;
const INITIAL_LIMIT_LIVE = 30; // Load more in live mode since sidebar has full height
const LOAD_MORE_COUNT = 10;

function EpisodeItem({ episode, fallbackImage, isCurrentEpisode, isInQueue, onAddToQueue, onNavigate, displayMode = 'title' }: EpisodeItemProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const { name: episodeName } = parseEpisodeTitle(episode.episodetitle);
  const dateInfo = formatEpisodeDate(episode.datetime);

  const handleClick = () => {
    onNavigate?.(episode.episodeid);
  };

  return (
    <div
      className={`flex items-center gap-2 p-2 rounded-lg
                 transition-all duration-150 ease-out
                 group
                 ${isCurrentEpisode
                   ? 'bg-[var(--color-primary)]/20 border border-[var(--color-primary)]/30'
                   : 'hover:bg-[var(--color-surface)]/60 border border-transparent'
                 }`}
    >
      {/* Thumbnail - links to episode */}
      <button
        type="button"
        onClick={handleClick}
        className="relative w-10 h-10 flex-shrink-0 rounded-md overflow-hidden bg-[var(--color-border)]
                   focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]"
      >
        <img
          src={episode.image || fallbackImage}
          alt=""
          loading="lazy"
          className={`w-full h-full object-cover transition-opacity duration-300
                     ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setImageLoaded(true)}
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            if (target.src !== fallbackImage) {
              target.src = fallbackImage;
            }
            setImageLoaded(true);
          }}
        />
        {/* Play overlay on hover */}
        {!isCurrentEpisode && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center
                          opacity-0 group-hover:opacity-100 transition-opacity duration-150">
            <HiPlay size={16} className="text-white" />
          </div>
        )}
      </button>

      {/* Info - different layouts based on displayMode */}
      <button
        type="button"
        onClick={handleClick}
        className="flex-1 min-w-0 text-left focus:outline-none"
      >
        {displayMode === 'date' ? (
          /* Date-focused: for "More from show" where show is always the same */
          <>
            <div className="flex items-baseline gap-1.5">
              <span className={`text-xs font-semibold
                              ${isCurrentEpisode ? 'text-[var(--color-primary)]' : 'text-[var(--color-text)]'}`}>
                {dateInfo.primary}
              </span>
              <span className="text-[10px] text-[var(--color-text-muted)]">
                {dateInfo.secondary}
              </span>
            </div>
            <p className="text-[10px] text-[var(--color-text-muted)] truncate mt-0.5">
              {episodeName}
            </p>
          </>
        ) : (
          /* Title-focused: for "Recent on station" where shows vary */
          <>
            <p className={`text-xs font-medium truncate leading-tight
                          ${isCurrentEpisode ? 'text-[var(--color-primary)]' : 'text-[var(--color-text)]'}`}>
              {episodeName}
            </p>
            <p className="text-[10px] text-[var(--color-text-muted)] truncate mt-0.5">
              {dateInfo.primary} {dateInfo.secondary}
            </p>
          </>
        )}
      </button>

      {/* Add to queue / Now playing indicator */}
      {isCurrentEpisode ? (
        <span className="flex-shrink-0 text-[10px] font-semibold text-[var(--color-primary)] uppercase">
          Now
        </span>
      ) : isInQueue ? (
        <span className="flex-shrink-0 p-1">
          <HiCheck size={14} className="text-[var(--color-primary)]" />
        </span>
      ) : (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onAddToQueue();
          }}
          className="flex-shrink-0 p-1 rounded-full
                     opacity-0 group-hover:opacity-100
                     hover:bg-[var(--color-primary)]/20
                     transition-all duration-150
                     focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]"
          aria-label="Add to queue"
          title="Add to queue"
        >
          <HiPlus size={14} className="text-[var(--color-primary)]" />
        </button>
      )}
    </div>
  );
}

function LoadMoreButton({ onClick, isLoading }: { onClick: () => void; isLoading: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={isLoading}
      className="w-full py-2 text-[10px] font-medium text-[var(--color-text-muted)]
                 hover:text-[var(--color-text)] hover:bg-[var(--color-surface)]/60
                 transition-colors rounded-lg
                 disabled:opacity-50 disabled:cursor-not-allowed
                 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]"
    >
      {isLoading ? 'Loading...' : 'Load More'}
    </button>
  );
}

export function Sidebar({ stream, show, currentEpisodeId, queuedEpisodeIds, onAddToQueue, onNavigate, isLiveMode = false, historyItems = [], onRemoveFromHistory }: SidebarProps) {
  const [stationEpisodes, setStationEpisodes] = useState<RecentEpisode[]>([]);
  const [showEpisodes, setShowEpisodes] = useState<RecentEpisode[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMoreStation, setLoadingMoreStation] = useState(false);
  const [loadingMoreShow, setLoadingMoreShow] = useState(false);
  const [stationHasMore, setStationHasMore] = useState(true);
  const [showHasMore, setShowHasMore] = useState(true);

  useEffect(() => {
    async function loadEpisodes() {
      setLoading(true);
      const limit = isLiveMode ? INITIAL_LIMIT_LIVE : INITIAL_LIMIT;
      try {
        const [stationRes, showRes] = await Promise.all([
          getRecentStationEpisodes(stream.streamid, limit),
          getRecentShowEpisodes(show.showid, limit),
        ]);
        setStationEpisodes(stationRes);
        setShowEpisodes(showRes);
        setStationHasMore(stationRes.length === limit);
        setShowHasMore(showRes.length === limit);
      } catch (err) {
        console.error('Failed to load recent episodes:', err);
      } finally {
        setLoading(false);
      }
    }

    loadEpisodes();
  }, [stream.streamid, show.showid, isLiveMode]);

  const loadMoreStation = useCallback(async () => {
    if (loadingMoreStation || !stationHasMore) return;
    setLoadingMoreStation(true);
    try {
      const newLimit = stationEpisodes.length + LOAD_MORE_COUNT;
      const moreEpisodes = await getRecentStationEpisodes(stream.streamid, newLimit);
      setStationEpisodes(moreEpisodes);
      setStationHasMore(moreEpisodes.length === newLimit);
    } catch (err) {
      console.error('Failed to load more station episodes:', err);
    } finally {
      setLoadingMoreStation(false);
    }
  }, [stream.streamid, stationEpisodes.length, loadingMoreStation, stationHasMore]);

  const loadMoreShow = useCallback(async () => {
    if (loadingMoreShow || !showHasMore) return;
    setLoadingMoreShow(true);
    try {
      const newLimit = showEpisodes.length + LOAD_MORE_COUNT;
      const moreEpisodes = await getRecentShowEpisodes(show.showid, newLimit);
      setShowEpisodes(moreEpisodes);
      setShowHasMore(moreEpisodes.length === newLimit);
    } catch (err) {
      console.error('Failed to load more show episodes:', err);
    } finally {
      setLoadingMoreShow(false);
    }
  }, [show.showid, showEpisodes.length, loadingMoreShow, showHasMore]);

  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center" role="status">
        <div
          className="w-6 h-6 border-2 border-[var(--color-border)]
                     border-t-[var(--color-primary)] rounded-full animate-spin"
        />
      </div>
    );
  }

  // Filter out current episode from history
  const filteredHistory = historyItems.filter(h => h.episodeId !== currentEpisodeId);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Continue Listening Section - hidden in live mode */}
      {!isLiveMode && filteredHistory.length > 0 && (
        <div className="flex-shrink-0 border-b border-[var(--color-border)]/50">
          <ContinueListening
            items={filteredHistory}
            fallbackImage={stream.image}
            onPlay={(episodeId) => onNavigate?.(episodeId)}
            onRemove={(episodeId) => onRemoveFromHistory?.(episodeId)}
          />
        </div>
      )}

      {/* Show Episodes Section - hidden in live mode (1:2 ratio with station section) */}
      {!isLiveMode && (
        <div className="flex-[1] min-h-0 flex flex-col">
          <div className="flex-shrink-0 px-3 py-2 border-b border-[var(--color-border)]/50">
            <h3 className="text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wide truncate">
              More from {show.title}
            </h3>
          </div>
          <div className="flex-1 overflow-y-auto px-2 py-2 space-y-1">
            {showEpisodes.length > 0 ? (
              <>
                {showEpisodes.map((ep) => (
                  <EpisodeItem
                    key={ep.episodeid}
                    episode={ep}
                    fallbackImage={stream.image}
                    isCurrentEpisode={ep.episodeid === currentEpisodeId}
                    isInQueue={queuedEpisodeIds.includes(ep.episodeid)}
                    onAddToQueue={() => onAddToQueue(ep)}
                    onNavigate={onNavigate}
                    displayMode="date"
                  />
                ))}
                {showHasMore && (
                  <LoadMoreButton onClick={loadMoreShow} isLoading={loadingMoreShow} />
                )}
              </>
            ) : (
              <p className="text-xs text-[var(--color-text-muted)] text-center py-4">
                No other episodes
              </p>
            )}
          </div>
        </div>
      )}

      {/* Station Episodes Section (2:1 ratio with show section) */}
      <div className={`flex-[2] min-h-0 flex flex-col ${!isLiveMode ? 'border-t border-[var(--color-border)]/50' : ''}`}>
        <div className="flex-shrink-0 px-3 py-2 border-b border-[var(--color-border)]/50">
          <h3 className="text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wide truncate">
            Recent on {stream.streamname}
          </h3>
        </div>
        <div className="flex-1 overflow-y-auto px-2 py-2 space-y-1">
          {stationEpisodes.length > 0 ? (
            <>
              {stationEpisodes.map((ep) => (
                <EpisodeItem
                  key={ep.episodeid}
                  episode={ep}
                  fallbackImage={stream.image}
                  isCurrentEpisode={ep.episodeid === currentEpisodeId}
                  isInQueue={queuedEpisodeIds.includes(ep.episodeid)}
                  onAddToQueue={() => onAddToQueue(ep)}
                  onNavigate={onNavigate}
                />
              ))}
              {stationHasMore && (
                <LoadMoreButton onClick={loadMoreStation} isLoading={loadingMoreStation} />
              )}
            </>
          ) : (
            <p className="text-xs text-[var(--color-text-muted)] text-center py-4">
              No recent episodes
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
